<template>
  <div class="flex flex-col" style="height: calc(100vh - 165px);">
    <div class="px-4 py-2.5 border-b border-gray-200 bg-white shrink-0 flex items-center gap-2">
      <span class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mr-2">{{ scopeLabel }}</span>
      <template v-if="loading && !patients.length">
        <div v-for="n in 6" :key="'sk' + n" class="h-8 w-28 bg-gray-100 rounded-md animate-pulse"></div>
      </template>
      <template v-else>
        <span class="mw-stat"><b>{{ counts.patients }}</b>Patients</span>
        <span class="mw-stat"><b>{{ counts.open_signals }}</b>Open signals</span>
        <span class="w-px h-6 bg-gray-200 mx-1"></span>
        <button class="mw-stat mw-stat-red" @click="toggleSignalType('redflag_routing')"><b>{{ counts.red_flag }}</b>With a red flag</button>
        <button class="mw-stat mw-stat-brand" @click="setLens('no_plan')"><b>{{ counts.no_plan }}</b>With an unplanned signal</button>
        <button class="mw-stat mw-stat-warn" @click="setLens('overdue')"><b>{{ counts.overdue }}</b>With overdue work</button>
        <button class="mw-stat mw-stat-brand" @click="setLens('handed_to_me')"><b>{{ counts.handed_to_me }}</b>Handed to you</button>
      </template>
      <button class="cm-icon-btn ml-auto" title="Refresh" @click="refreshAll">
        <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 12px;" />
      </button>
    </div>

    <div class="px-4 pt-2 bg-gray-50 border-b border-gray-200 shrink-0">
      <Worklist_FilterBar
        storage-key="my_tasks_filters"
        :demographic-filters="[]"
        @filter-changed="setEntityFilters"
      />
    </div>

    <div class="px-4 py-2 flex items-end flex-wrap gap-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Search</label>
        <input
          v-model="searchQuery" data-demo-search
          type="text"
          placeholder="Patient name or MBI..."
          class="sensitive w-52 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
      <div class="cm-seg mw-seg">
        <button
          v-for="opt in scopeOptions"
          :key="opt.key"
          :class="{ on: scope === opt.key && ownerFilter == null }"
          @click="setScope(opt.key)"
        >{{ opt.label }}</button>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Care Manager</label>
        <select
          v-model="ownerFilter"
          class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 max-w-[180px]"
        >
          <option :value="null">—</option>
          <option value="unassigned">Unassigned</option>
          <option v-for="m in sortedMembers" :key="m.id" :value="m.id" class="sensitive">{{ m.name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Signal type</label>
        <select
          v-model="signalType"
          class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          <option :value="null">All types</option>
          <option v-for="t in signalTypeOptions" :key="t.key" :value="t.key">{{ t.label }}{{ typeCounts[t.key] ? ` (${typeCounts[t.key]})` : '' }}</option>
        </select>
      </div>
      <button :class="chipClass(lens === 'no_plan')" @click="setLens('no_plan')">No plan<span class="mw-n mw-n-brand">{{ counts.no_plan }}</span></button>
      <button :class="chipClass(lens === 'overdue')" @click="setLens('overdue')">Overdue<span class="mw-n mw-n-warn">{{ counts.overdue }}</span></button>
      <button :class="chipClass(lens === 'handed_to_me')" @click="setLens('handed_to_me')">Handed to me<span class="mw-n mw-n-brand">{{ counts.handed_to_me }}</span></button>
      <button :class="['ml-auto', chipClass(includeResolved)]" @click="includeResolved = !includeResolved">Show resolved</button>
    </div>

    <MyTasksCalendar
      :scope-params="calendarParams"
      :selected-due="due"
      :filtered-total="total"
      :expanded-prop="calendarOpen"
      :refresh-key="refreshKey"
      @select-due="setDue"
      @patient-events="patientEvents = $event"
      @expanded="onCalendarExpanded"
    />

    <div ref="split" class="flex-1 flex overflow-hidden">
      <div
        :style="{ width: selectedPatient ? splitPct + '%' : '100%' }"
        class="mw-stream flex flex-col min-w-0 overflow-y-auto"
      >
        <template v-if="loading && !patients.length">
          <div v-for="n in 4" :key="'ph' + n" class="mw-card p-4 space-y-2">
            <div class="h-4 w-48 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 w-80 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 w-72 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </template>

        <div v-else-if="!patients.length" class="py-16 text-center text-gray-400">
          <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-2xl mb-2 text-green-300" />
          <div class="text-sm">{{ emptyStateText }}</div>
        </div>

        <div
          v-for="p in patients"
          v-else
          :key="p.entity_id"
          class="mw-card"
          :class="{ 'mw-card-sel': selectedPatient && selectedPatient.entity_id === p.entity_id }"
          @click="openPatient(p)"
        >
          <div class="mw-card-top">
            <span class="mw-name sensitive">{{ p.patient_name }}</span>
            <span class="text-[11px] text-gray-500 sensitive">{{ p.patient_identifier || '' }}</span>
            <span class="ml-auto flex items-center gap-1.5">
              <span v-if="p.red_flag_count" class="mw-pill mw-pill-red">{{ p.red_flag_count }} red flag{{ p.red_flag_count === 1 ? '' : 's' }}</span>
              <span v-if="p.no_plan" class="mw-pill mw-pill-brand">{{ p.no_plan }} no plan</span>
              <span v-if="p.overdue" class="mw-pill mw-pill-warn">Overdue</span>
              <span v-if="!p.red_flag_count && !p.no_plan && !p.overdue" class="mw-pill">{{ p.open_signals }} signal{{ p.open_signals === 1 ? '' : 's' }}</span>
            </span>
          </div>

          <div v-if="(patientEvents[p.entity_id] || []).length" class="mw-appts">
            <span
              v-for="(e, i) in patientEvents[p.entity_id].slice(0, 3)"
              :key="i"
              class="mw-appt"
              :class="{ 'mw-appt-booked': e.kind === 'booked' }"
            >
              <FontAwesomeIcon :icon="['fas', e.kind === 'appointment' ? 'stethoscope' : 'phone']" class="mw-appt-ico" />
              <b>{{ eventWhen(e) }}</b>
              <span class="sensitive">{{ e.kind === 'appointment' ? [e.label, e.clinician].filter(Boolean).join(' · ') : e.label }}</span>
            </span>
          </div>

          <div class="mw-tree">
            <ul>
              <li v-for="g in p.programs" :key="programKey(p, g)">
                <div class="mw-row mw-prog" :class="{ 'mw-prog-closed': !g.is_open }">
                  <span class="mw-pdot"></span>
                  <span class="mw-prog-name">{{ g.program }}</span>
                  <template v-if="g.is_open && g.opportunity_id">
                    <span v-if="g.stage" class="mw-prog-stage">{{ g.stage }}</span>
                    <span v-if="g.due_date" class="mw-prog-due" :class="{ 'mw-over': g.is_overdue }">{{ g.is_overdue ? 'Overdue' : 'Due' }} {{ shortDate(g.due_date) }}</span>
                    <span v-if="g.owner" class="mw-av sensitive" :style="avatarStyle(g.owner.id)" :title="g.owner.name">{{ g.owner.initials }}</span>
                  </template>
                  <span v-else-if="g.closed_at" class="mw-prog-stage">closed {{ shortDate(g.closed_at) }}</span>
                  <span v-else-if="g.opportunity_id" class="mw-prog-stage">open, no active step</span>
                  <button
                    v-if="hiddenCount(p, g)"
                    class="mw-more"
                    @click.stop="toggleExpanded(p, g)"
                  >{{ isExpanded(p, g) ? 'Hide' : `${hiddenCount(p, g)} earlier signal${hiddenCount(p, g) === 1 ? '' : 's'}` }}</button>
                  <span v-else class="mw-prog-count">{{ programCount(g) }}</span>
                </div>
                <ul v-if="g.notes.length || visibleSignals(p, g).length">
                  <li v-for="n in g.notes" :key="'n' + n.id" class="mw-li-note">
                    <div class="mw-note" :class="{ 'mw-note-new': n.addressed_to_me }">
                      <FontAwesomeIcon :icon="['fas', 'arrow-right']" class="mw-note-ico" />
                      <div class="min-w-0 flex-1">
                        <div class="mw-note-meta"><b class="sensitive">{{ n.author || 'Someone' }}</b> {{ n.verb }} · {{ relTime(n.at) }}</div>
                        <DemoText tag="div" class="mw-note-text" :text="n.text" :record="p.patient_identifier" />
                        <div v-if="closing.id === n.id" class="mt-1.5" @click.stop>
                          <CareGraphCloseForm
                            :mode="closing.mode"
                            :saving="saving"
                            :error="closeError"
                            @submit="payload => submitClose(n.id, payload)"
                            @cancel="cancelClose"
                          />
                        </div>
                      </div>
                      <button
                        v-if="n.kind === 'transition' || n.kind === 'closure'"
                        class="mw-act"
                        title="Mark this handoff as read and done"
                        @click.stop="openClose(n.id, 'acknowledge')"
                      >Acknowledge</button>
                      <button
                        v-else-if="n.kind === 'request'"
                        class="mw-act"
                        @click.stop="openClose(n.id, 'todo')"
                      >Close</button>
                    </div>
                  </li>
                  <li v-for="s in visibleSignals(p, g)" :key="'s' + s.id">
                    <div class="mw-row mw-sig" :class="{ 'mw-sig-inactive': !s.active }">
                      <span class="mw-typ" :class="'mw-typ-' + typeClass(s.type)">{{ typeLabel(s.type) }}</span>
                      <span class="mw-sig-title sensitive" data-demo-blur>{{ s.title }}</span>
                      <span class="flex gap-1.5 shrink-0">
                        <span v-if="s.no_plan" class="mw-pill mw-pill-brand">No plan</span>
                        <span v-if="s.is_overdue" class="mw-pill mw-pill-warn">Overdue</span>
                        <span v-if="!s.is_open" class="mw-pill mw-pill-ok">{{ s.closure_state === 'no_longer_relevant' ? 'No longer relevant' : 'Resolved' }}</span>
                        <button v-if="s.closure_state" class="mw-act" @click.stop="reopen(s.id)">Reopen</button>
                        <button v-else-if="s.is_open" class="mw-act" title="Close as resolved or no longer relevant" @click.stop="openClose(s.id, 'signal')">Close</button>
                      </span>
                    </div>
                    <div v-if="closing.id === s.id" class="mw-form" @click.stop>
                      <CareGraphCloseForm
                        mode="signal"
                        :open-todo-count="s.todos.filter(t => t.state === 'open' || t.state === 'attempted').length"
                        :saving="saving"
                        :error="closeError"
                        @submit="payload => submitClose(s.id, payload)"
                        @cancel="cancelClose"
                      />
                    </div>
                    <ul v-if="s.todos.length">
                      <li v-for="t in s.todos" :key="'t' + t.id" class="mw-li-todo">
                        <div class="mw-row mw-todo" :class="{ 'mw-todo-closed': t.state === 'done' || t.state === 'unable' }">
                          <span
                            class="mw-cbx"
                            :class="['mw-cbx-' + t.state, { 'mw-cbx-click': t.state === 'open' || t.state === 'attempted' }]"
                            :title="t.state === 'open' || t.state === 'attempted' ? 'Close this to-do' : ''"
                            @click.stop="(t.state === 'open' || t.state === 'attempted') && openClose(t.id, 'todo')"
                          >
                            <template v-if="t.state === 'done'">✓</template>
                            <template v-else-if="t.state === 'unable'">✕</template>
                          </span>
                          <span class="mw-todo-title sensitive" data-demo-blur>{{ t.title }}</span>
                          <span v-if="gradeLabel(t.closure_grade)" class="mw-grade" :class="{ 'mw-grade-v': t.closure_grade === 'verified_terminal' }">{{ gradeLabel(t.closure_grade) }}</span>
                          <span class="mw-todo-date" :class="{ 'mw-over': t.is_overdue }">{{ todoDate(t) }}</span>
                          <span v-if="t.owner" class="mw-av sensitive" :style="avatarStyle(t.owner.id)" :title="t.owner.name">{{ t.owner.initials }}</span>
                          <button v-if="t.state === 'done' || t.state === 'unable'" class="mw-act" @click.stop="reopen(t.id)">Reopen</button>
                        </div>
                        <div v-if="closing.id === t.id" class="mw-form" @click.stop>
                          <CareGraphCloseForm
                            mode="todo"
                            :default-title="t.title"
                            :saving="saving"
                            :error="closeError"
                            @submit="payload => submitClose(t.id, payload)"
                            @cancel="cancelClose"
                          />
                        </div>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>

        <div
          v-if="total > pageSize"
          class="mw-pager"
        >
          <span>{{ counts.patients }} patients · {{ counts.open_signals }} open signals · showing {{ pageStart }}–{{ pageEnd }} of {{ total }}</span>
          <div class="flex items-center gap-2">
            <button class="cm-icon-btn" :disabled="page <= 1" title="Previous page" @click="goToPage(page - 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 11px;" />
            </button>
            <span>{{ page }} / {{ pageCount }}</span>
            <button class="cm-icon-btn" :disabled="!hasMore" title="Next page" @click="goToPage(page + 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 11px;" />
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="selectedPatient"
        class="w-1 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors shrink-0"
        @mousedown="startDrag"
      ></div>

      <div
        v-if="selectedPatient"
        :style="{ width: (100 - splitPct) + '%' }"
        class="flex flex-col overflow-hidden"
      >
        <div class="flex-1 overflow-hidden">
          <Patient_Drawer
            :key="selectedPatient.entity_id"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatient"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="drawerEnabledTabs"
            :show-header="true"
            default-tab="care_graph"
            metric-category="Care Management"
            @show-message="showMessage"
            @task-updated="loadPatients"
            @close="closeDrawer"
          />
        </div>
      </div>
    </div>

    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="font-medium">{{ message.text }}</div>
      </div>
    </transition>
  </div>
</template>

<script>
import '@/assets/cm-tokens.css';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import MyTasksCalendar from '@/components/Care_Management/MyTasksCalendar.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import DemoText from '@/components/DemoMode/DemoText.vue';
import CareGraphCloseForm from '@/components/Care_Management/CareGraphCloseForm.vue';

const SIGNAL_TYPES = [
  { key: 'redflag_routing', label: 'Red flag', cls: 'rf' },
  { key: 'decompensation_escalation', label: 'Decompensation', cls: 'dc' },
  { key: 'critical_med_access', label: 'Med access', cls: 'ma' },
  { key: 'timely_followup', label: 'Follow-up', cls: 'fu' },
  { key: 'home_services', label: 'Home services', cls: 'hs' },
  { key: 'other', label: 'Other', cls: 'ot' },
];
const GRADE_LABELS = { verified_terminal: 'verified', soft_action: 'soft', in_progress_step: 'in progress' };
const AVATAR_COLORS = ['#5B7DB1', '#C46A3B', '#4F9A72', '#7B6AA8', '#B0616E', '#3E8A9A', '#8A7A3E'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
  name: 'CareManagementMyTasks',
  components: { DemoText, FontAwesomeIcon, Patient_Drawer, MyTasksCalendar, Worklist_FilterBar, CareGraphCloseForm },
  mixins: [demoProtectedFields],
  // The patient name/MBI search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['searchQuery'],
  data() {
    return {
      loading: false,
      saving: false,
      closing: { id: null, mode: null },
      closeError: '',
      patients: [],
      total: 0,
      page: 1,
      pageSize: 25,
      hasMore: false,
      serverCounts: null,
      typeCounts: {},
      searchQuery: '',
      scope: 'my_patients',
      ownerFilter: null,
      signalType: null,
      lens: null,
      entityFilters: null,
      includeResolved: false,
      due: null,
      patientEvents: {},
      refreshKey: 0,
      expanded: {},
      members: [],
      selectedPatient: null,
      splitPct: parseInt(localStorage.getItem('cm_split_pct') || '50'),
      message: null,
      signalTypeOptions: SIGNAL_TYPES,
      scopeOptions: [
        { key: 'my_patients', label: 'My patients' },
        { key: 'my_todos', label: 'My to-dos' },
        { key: 'all', label: 'All' },
      ],
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    drawerEnabledTabs() {
      const base = ['details', 'utilization', 'programs', 'engagement', 'care_graph', 'care_team'];
      return this.current_tenant_slug === 'ihci' ? [...base, 'insights'] : base;
    },
    sortedMembers() {
      return [...this.members].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    counts() {
      return this.serverCounts || { patients: 0, open_signals: 0, red_flag: 0, no_plan: 0, overdue: 0, handed_to_me: 0 };
    },
    scopeLabel() {
      if (this.ownerFilter === 'unassigned') return 'Unassigned';
      if (this.ownerFilter != null) {
        const m = this.members.find(x => String(x.id) === String(this.ownerFilter));
        return m ? m.name : 'Care manager';
      }
      return { my_patients: 'My patients', my_todos: 'My to-dos', all: 'All patients' }[this.scope];
    },
    calendarOpen() {
      return this.$route.hash === '#month';
    },
    calendarParams() {
      const params = { scope: this.scope };
      if (this.ownerFilter != null) params.owner = String(this.ownerFilter);
      if (this.entityFilters) params.entity_filters = JSON.stringify(this.entityFilters);
      return params;
    },
    queryParams() {
      const params = { scope: this.scope, page: this.page, page_size: this.pageSize };
      if (this.due) params.due = this.due;
      if (this.ownerFilter != null) params.owner = String(this.ownerFilter);
      if (this.entityFilters) params.entity_filters = JSON.stringify(this.entityFilters);
      if (this.signalType) params.signal_type = this.signalType;
      if (this.lens) params.lens = this.lens;
      if (this.includeResolved) params.include_resolved = 1;
      if (this.searchQuery.trim()) params.q = this.searchQuery.trim();
      return params;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageStart() {
      return this.total ? (this.page - 1) * this.pageSize + 1 : 0;
    },
    pageEnd() {
      return Math.min(this.total, this.page * this.pageSize);
    },
    emptyStateText() {
      if (this.due) return 'No patients with work due then';
      if (this.lens === 'no_plan') return 'Every open signal has a plan';
      if (this.lens === 'overdue') return 'Nothing overdue';
      if (this.lens === 'handed_to_me') return 'Nothing has been handed to you';
      if (this.scope === 'my_todos' && this.ownerFilter == null) return 'No open to-dos assigned to you';
      return 'No patients in active care management match these filters';
    },
  },
  watch: {
    scope() { this.reloadFromStart(); },
    ownerFilter() { this.reloadFromStart(); },
    signalType() { this.reloadFromStart(); },
    lens() { this.reloadFromStart(); },
    includeResolved() { this.reloadFromStart(); },
    due() { this.reloadFromStart(); },
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.reloadFromStart(), 300);
    },
  },
  mounted() {
    this.loadPatients();
    this.loadMembers();
    this._onMessage = (event) => {
      if (event.data?.type === 'clinical-session-saved') this.loadPatients();
    };
    window.addEventListener('message', this._onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._searchTimer);
  },
  methods: {
    chipClass(active) {
      return [
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
        active ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
      ];
    },
    setScope(scope) {
      this.ownerFilter = null;
      this.scope = scope;
    },
    setLens(lens) {
      this.lens = this.lens === lens ? null : lens;
    },
    toggleSignalType(key) {
      this.signalType = this.signalType === key ? null : key;
    },
    setDue(due) {
      this.due = due;
    },
    onCalendarExpanded(open) {
      const hash = open ? '#month' : '';
      if ((this.$route.hash || '') !== hash) this.$router.replace({ hash }).catch(() => {});
    },
    eventWhen(e) {
      const [y, m, d] = e.date.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.round((dt - today) / 86400000);
      const day = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow'
        : diff < 7 ? dt.toLocaleDateString('en-US', { weekday: 'short' }) : this.shortDate(e.date);
      return e.time ? `${day} ${e.time}` : day;
    },
    setEntityFilters(payload) {
      const next = payload?.entity_filters ?? null;
      if (JSON.stringify(next) === JSON.stringify(this.entityFilters)) return;
      this.entityFilters = next;
      this.reloadFromStart();
    },
    reloadFromStart() {
      this.page = 1;
      this.loadPatients();
    },
    refreshAll() {
      this.refreshKey += 1;
      this.loadPatients();
    },
    goToPage(n) {
      this.page = Math.max(1, n);
      this.loadPatients();
    },
    openClose(id, mode) {
      this.closeError = '';
      this.closing = { id, mode };
    },
    cancelClose() {
      this.closing = { id: null, mode: null };
      this.closeError = '';
    },
    apiError(e, fallback) {
      const d = e?.response?.data;
      if (d && typeof d === 'object') {
        const first = Object.values(d).flat().find(v => typeof v === 'string');
        if (first) return first;
      }
      return fallback;
    },
    async submitClose(id, payload) {
      if (this.saving) return;
      this.saving = true;
      this.closeError = '';
      try {
        await this.$axios.post(`/api/careplannodes/${id}/close/`, payload);
        this.cancelClose();
        await this.loadPatients({ silent: true });
      } catch (e) {
        this.closeError = this.apiError(e, 'Could not close');
      } finally {
        this.saving = false;
      }
    },
    async reopen(id) {
      if (this.saving) return;
      this.saving = true;
      try {
        await this.$axios.post(`/api/careplannodes/${id}/reopen/`);
        await this.loadPatients({ silent: true });
      } catch (e) {
        this.showMessage(this.apiError(e, 'Could not reopen'), 'error');
      } finally {
        this.saving = false;
      }
    },
    async loadPatients({ silent = false } = {}) {
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1);
      if (!silent) this.loading = true;
      try {
        const { data } = await this.$axios.get('/api/careplannodes/patients/', { params: this.queryParams });
        if (seq !== this._reqSeq) return;
        if (data?.success) {
          this.patients = data.patients || [];
          this.total = data.total || 0;
          this.hasMore = !!data.has_more;
          this.serverCounts = data.counts || null;
          this.typeCounts = data.signal_type_counts || {};
        }
      } catch (e) {
        if (seq !== this._reqSeq) return;
        console.error('Error loading patients:', e);
        this.showMessage('Could not load patients', 'error');
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    async loadMembers() {
      try {
        const { data } = await this.$axios.get('/api/careplannodes/addressees/');
        this.members = (data?.members || []).filter(r => r.kind === 'person').map(r => ({ id: r.id, name: r.name || 'Unknown' }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    programKey(p, g) {
      return `${p.entity_id}-${g.opportunity_id || 'other'}`;
    },
    isExpanded(p, g) {
      return !!this.expanded[this.programKey(p, g)];
    },
    toggleExpanded(p, g) {
      const k = this.programKey(p, g);
      this.expanded = { ...this.expanded, [k]: !this.expanded[k] };
    },
    visibleSignals(p, g) {
      return this.isExpanded(p, g) ? g.signals : g.signals.filter(s => s.active || !s.is_open);
    },
    hiddenCount(p, g) {
      return g.signals.filter(s => !s.active && s.is_open).length;
    },
    programCount(g) {
      const parts = [];
      const n = g.active_signals;
      if (n) parts.push(`${n} signal${n === 1 ? '' : 's'}`);
      if (g.notes.length) parts.push(`${g.notes.length} note${g.notes.length === 1 ? '' : 's'}`);
      return parts.join(' · ');
    },
    typeLabel(key) {
      return (SIGNAL_TYPES.find(t => t.key === key) || SIGNAL_TYPES[SIGNAL_TYPES.length - 1]).label;
    },
    typeClass(key) {
      return (SIGNAL_TYPES.find(t => t.key === key) || SIGNAL_TYPES[SIGNAL_TYPES.length - 1]).cls;
    },
    gradeLabel(g) {
      return GRADE_LABELS[g] || '';
    },
    avatarStyle(id) {
      return { background: AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length] };
    },
    shortDate(v) {
      const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${MON[Number(m[2]) - 1]} ${Number(m[3])}` : '';
    },
    relTime(iso) {
      if (!iso) return '';
      const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
      if (mins < 60) return mins < 1 ? 'just now' : `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      if (days === 1) return 'yesterday';
      return days < 7 ? `${days}d ago` : this.shortDate(iso);
    },
    todoDate(t) {
      if (t.state === 'done') return `Done ${this.shortDate(t.done_at)}`;
      if (t.state === 'unable') return `Unable ${this.shortDate(t.done_at)}`;
      if (t.due_date) return `${t.is_overdue ? 'Overdue' : 'Due'} ${this.shortDate(t.due_date)}`;
      if (t.state === 'attempted') return `Attempted ${this.relTime(t.updated_at)}`;
      return '';
    },
    openPatient(p) {
      this.selectedPatient = {
        entity_id: p.entity_id,
        patient_identifier: p.patient_identifier,
        patient_name: p.patient_name,
      };
    },
    closeDrawer() {
      this.selectedPatient = null;
    },
    startDrag(e) {
      e.preventDefault();
      const container = this.$refs.split;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        this.splitPct = Math.round(Math.max(25, Math.min(75, pct)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('cm_split_pct', String(this.splitPct));
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => { this.message = null; }, 5000);
    },
  },
};
</script>

<style scoped>
.mw-seg button { padding: 5px 12px; font-size: 12px; }
.mw-stat {
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px;
  border: 1px solid #E5E7EB; border-radius: 6px; background: #fff;
  font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #6B7280;
}
.mw-stat b { font-size: 15px; color: #111827; letter-spacing: 0; font-variant-numeric: tabular-nums; }
.mw-stat-red { background: #FDECEC; border-color: #F8D0D0; color: #B42318; }
.mw-stat-red b { color: #B42318; }
.mw-stat-brand { background: #FEF1EB; border-color: #FDE3D8; color: #B9431B; }
.mw-stat-brand b { color: #B9431B; }
.mw-stat-warn { background: #FDF4E7; border-color: #F6E2BE; color: #B45309; }
.mw-stat-warn b { color: #B45309; }
.mw-n {
  font-size: 10.5px; font-weight: 700; min-width: 17px; height: 17px; padding: 0 5px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center; background: #F3F4F6; color: #4B5563;
}
.mw-n-brand { background: #FEF1EB; color: #B9431B; }
.mw-n-warn { background: #FDF4E7; color: #B45309; }

.mw-stream { background: #F6F7F8; padding: 12px; gap: 12px; }
.mw-card {
  flex-shrink: 0; background: #fff; border: 1px solid #E5E7EB; border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04); cursor: pointer; overflow: hidden;
}
.mw-card:hover { border-color: #D1D5DB; }
.mw-card-sel { border-color: #F76734; box-shadow: 0 0 0 3px rgba(247, 103, 52, .12); }
.mw-card-sel:hover { border-color: #F76734; }
.mw-card-top { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #F3F4F6; }
.mw-name { font-size: 13.5px; font-weight: 600; color: #1D4ED8; text-transform: uppercase; letter-spacing: .01em; }
.mw-pill {
  display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 600; padding: 2px 7px;
  border-radius: 999px; background: #F3F4F6; color: #4B5563; white-space: nowrap;
}
.mw-pill-red { background: #FDECEC; color: #B42318; }
.mw-pill-brand { background: #FEF1EB; color: #B9431B; }
.mw-pill-warn { background: #FDF4E7; color: #B45309; }
.mw-pill-ok { background: #E9F7F0; color: #0E6B43; }

.mw-appts { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px 0; }
.mw-appt {
  display: inline-flex; align-items: center; gap: 6px; height: 22px; padding: 0 8px; border-radius: 999px;
  font-size: 11px; background: #EFF8F3; color: #0E6B43; white-space: nowrap;
}
.mw-appt b { font-weight: 600; }
.mw-appt-booked { background: #EEF4FF; color: #1D4ED8; }
.mw-appt-ico { font-size: 9.5px; }
.mw-tree { padding: 4px 16px 12px; }
.mw-tree ul { list-style: none; margin: 0; padding: 0 0 0 11px; }
.mw-tree > ul { padding-left: 4px; }
.mw-tree li { position: relative; padding-left: 18px; }
.mw-tree li::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; border-left: 1.5px solid #E5E7EB; }
.mw-tree li:last-child::before { bottom: auto; height: 17px; }
.mw-tree li::after { content: ""; position: absolute; left: 0; top: 17px; width: 12px; border-top: 1.5px solid #E5E7EB; }
.mw-tree li.mw-li-todo::after { top: 15px; }
.mw-tree li.mw-li-todo:last-child::before { height: 15px; }
.mw-tree li.mw-li-note::after { top: 21px; }
.mw-tree li.mw-li-note:last-child::before { height: 21px; }

.mw-row { display: flex; align-items: center; gap: 8px; min-height: 34px; min-width: 0; }
.mw-pdot { width: 8px; height: 8px; border-radius: 50%; background: #389A6C; flex-shrink: 0; }
.mw-prog-name { font-size: 12.5px; font-weight: 600; color: #111827; white-space: nowrap; }
.mw-prog-stage { font-size: 12px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.mw-prog-due { font-size: 11.5px; color: #6B7280; white-space: nowrap; font-variant-numeric: tabular-nums; }
.mw-prog-count { margin-left: auto; font-size: 11px; color: #9CA3AF; white-space: nowrap; }
.mw-prog-closed .mw-pdot { background: #C7C9CC; }
.mw-prog-closed .mw-prog-name { color: #6B7280; font-weight: 500; }
.mw-over { color: #B45309 !important; font-weight: 500; }
.mw-more { margin-left: auto; font-size: 11px; color: #4B5563; background: none; border: 0; padding: 0; cursor: pointer; white-space: nowrap; }
.mw-more:hover { color: #111827; text-decoration: underline; }
.mw-av {
  width: 18px; height: 18px; min-width: 18px; border-radius: 50%; display: inline-flex; align-items: center;
  justify-content: center; font-size: 8.5px; font-weight: 700; color: #fff; flex-shrink: 0;
}

.mw-typ {
  font-size: 9.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; padding: 1px 5px;
  border-radius: 3px; flex-shrink: 0; min-width: 92px; text-align: center;
}
.mw-typ-rf { background: #FDECEC; color: #B42318; }
.mw-typ-dc { background: #FEF1EB; color: #B9431B; }
.mw-typ-ma { background: #FDF4E7; color: #B45309; }
.mw-typ-fu { background: #EEF4FF; color: #1D4ED8; }
.mw-typ-hs { background: #EFF8F3; color: #0E6B43; }
.mw-typ-ot { background: #F3F4F6; color: #6B7280; }
.mw-sig-title { font-size: 12.5px; color: #111827; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mw-sig-inactive { opacity: .6; }

.mw-todo { min-height: 30px; }
.mw-cbx {
  width: 14px; height: 14px; border: 1.5px solid #9CA3AF; border-radius: 3px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; background: #fff; font-size: 9px; font-weight: 700;
}
.mw-cbx-attempted { border-color: #2563EB; background: linear-gradient(135deg, #DBEAFE 50%, #fff 50%); }
.mw-cbx-done { border-color: #0E6B43; background: #0E6B43; color: #fff; }
.mw-cbx-unable { border-color: #B42318; background: #FDECEC; color: #B42318; }
.mw-cbx-click { cursor: pointer; }
.mw-cbx-click:hover { border-color: #0E6B43; background: #E7F4EC; }
.mw-act { flex-shrink: 0; padding: 0 6px; border: 1px solid #E5E7EB; border-radius: 4px; font-size: 10.5px; color: #6B7280; background: #fff; }
.mw-act:hover { color: #0E6B43; border-color: #86C9A5; }
.mw-form { margin: 4px 0 6px 18px; padding: 8px; border: 1px solid #E5E7EB; border-radius: 6px; background: #FAFBFA; }
.mw-todo-title { font-size: 12.5px; color: #111827; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mw-todo-closed .mw-todo-title { color: #9CA3AF; text-decoration: line-through; }
.mw-todo-date { font-size: 11px; color: #6B7280; white-space: nowrap; font-variant-numeric: tabular-nums; }
.mw-grade { font-size: 10.5px; font-weight: 600; padding: 1px 6px; border-radius: 4px; background: #F3F4F6; color: #6B7280; flex-shrink: 0; }
.mw-grade-v { background: #E9F7F0; color: #0E6B43; }

.mw-note {
  display: flex; align-items: flex-start; gap: 8px; padding: 6px 10px; margin: 4px 0;
  background: #F5F8FF; border: 1px solid #DCE6FA; border-radius: 6px; min-width: 0;
}
.mw-note-new { background: #FFF7F3; border-color: #FDE3D8; }
.mw-note-ico { color: #3B6FD9; font-size: 11px; margin-top: 3px; flex-shrink: 0; }
.mw-note-meta { font-size: 11.5px; color: #4B5563; }
.mw-note-meta b { color: #111827; font-weight: 600; }
.mw-note-text { font-size: 12.5px; color: #111827; line-height: 1.4; margin-top: 1px; }

.mw-pager {
  position: sticky; bottom: -12px; margin: 0 -12px -12px; z-index: 3; flex-shrink: 0;
  display: flex; justify-content: space-between; align-items: center; padding: 8px 16px;
  background: #fff; border-top: 1px solid #E5E7EB; font-size: 11.5px; color: #6B7280;
}
</style>
